import {GRANITE_2, GRANITE_3} from '@gaia/colors';
import theme from '@gaia/theme';
import {Typography, TypographyProps} from '@mui/material';
import type {CSSProperties} from 'react';

// Workaround to support setting font variation styles on Inter.
// MUI doesn't currently provide a way to theme variable fonts out of the box.
export const typographyStyles = {
    bold: {
        fontWeight: theme.typography.fontWeightBold as CSSProperties['fontWeight'],
        fontVariationSettings: `"wght" ${theme.typography.fontWeightBold}`,
    },
    semibold: {
        fontWeight: theme.typography.fontWeightMedium as CSSProperties['fontWeight'],
        fontVariationSettings: `"wght" ${theme.typography.fontWeightMedium}`,
    },
};

export function H2({...props}) {
    const {style, ...restProps} = props;
    return (
        <Typography
            variant='h2'
            style={{
                ...typographyStyles.bold,
                wordBreak: 'break-word',
                color: GRANITE_2,
                ...style,
            }}
            {...restProps}
        />
    );
}

export function H6({...props}) {
    const {style, ...restProps} = props;
    return (
        <Typography
            variant='h5'
            style={{
                ...typographyStyles.bold,
                wordBreak: 'break-word',
                color: GRANITE_2,
                ...style,
            }}
            {...restProps}
        />
    );
}

interface BodyProps extends TypographyProps {
    /** If `true`, the text will render in the theme's default bold font-weight. */
    bold?: boolean;
    /** An HTML element to use as a root node. */
    component?: string;
}

export function Body1({...props}: BodyProps) {
    const {bold, style, ...muiTypographyProps} = props;
    return (
        <Typography
            variant='body1'
            style={{
                ...(bold && typographyStyles.bold),
                fontSize: 16,
                ...style,
            }}
            {...muiTypographyProps}
        />
    );
}

export function Body2({...props}: BodyProps) {
    const {bold, style, ...muiTypographyProps} = props;
    return (
        <Typography
            variant='body1'
            style={{
                ...(bold && typographyStyles.bold),
                fontSize: 14,
                ...style,
            }}
            {...muiTypographyProps}
        />
    );
}

export function Caption({...props}) {
    const {bold, style, ...muiTypographyProps} = props;
    return (
        <Typography
            variant='caption'
            style={{
                ...(bold && typographyStyles.bold),
                color: GRANITE_3,
                fontSize: 12,
                ...style,
            }}
            {...muiTypographyProps}
        />
    );
}

export default {
    Body1,
    Body2,
    Caption,
    H2,
    H6,
};
